<template>
  <div class="users-list-container">
    <b-row>
      <b-col cols="12" xl="12">
        <transition name="slide">
          <b-card>
            <template #header>
              <div class="users-card-header">
                <h5 class="m-0">Usuários</h5>
                <b-button
                  v-if="canCreate"
                  size="sm"
                  variant="primary"
                  @click="onNewUserClicked"
                  >Novo Usuário
                </b-button>
              </div>
            </template>
            <b-card-body>
              <div class="filters-section">
                <b-form-group label="Pesquisar" class="search-filter">
                  <b-input-group>
                    <b-input-group-prepend>
                      <b-input-group-text>
                        <i class="fas fa-search" aria-hidden="true"></i>
                      </b-input-group-text>
                    </b-input-group-prepend>
                    <b-form-input
                      v-model="filter"
                      type="search"
                      placeholder="Digite nome ou email para pesquisar"
                      @input="debounceFilter"
                      @keyup.enter="applyFilter"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-button :disabled="!filter" @click="clearFilter"
                        >Limpar</b-button
                      >
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
              </div>

              <div class="table-container">
                <b-table
                  :items="data.docs"
                  :fields="headers"
                  :busy="loading"
                  :sort-by.sync="sortBy"
                  :sort-desc.sync="sortDesc"
                  :tbody-tr-class="rowClass"
                  thead-class="users-header"
                  borderless
                  hover
                  responsive
                  show-empty
                  no-local-sorting
                  @sort-changed="onSortChanged"
                  @row-clicked="onRowClicked"
                >
                  <template #cell(company)="slotData">
                    <span class="company-name">{{
                      slotData.item.company
                        ? slotData.item.company.fantasyName
                        : ""
                    }}</span>
                  </template>
                  <template #cell(roleName)="slotData">
                    <span class="role-text">{{
                      slotData.item.roleName || ""
                    }}</span>
                  </template>
                  <template #cell(name)="slotData">
                    <strong class="user-name">{{
                      slotData.item.fullname
                    }}</strong>
                  </template>
                  <template #cell(position)="slotData">
                    <span class="position-text">{{
                      slotData.item.position || ""
                    }}</span>
                  </template>
                  <template #cell(lastLogin)="slotData">
                    <span class="date-text">{{
                      slotData.item.lastLogin | datetime
                    }}</span>
                  </template>
                  <template #cell(status)="slotData">
                    <b-badge :variant="getBadge(slotData.item.active)">
                      {{ slotData.item.active ? "Ativo" : "Inativo" }}
                    </b-badge>
                  </template>

                  <template #table-busy>
                    <div class="text-center py-5">
                      <b-spinner></b-spinner>
                      <span class="ml-2">Carregando...</span>
                    </div>
                  </template>
                  <template #empty>
                    <div class="text-muted text-center py-5">
                      <i class="fas fa-users fa-2x"></i>
                      <p class="mt-3">Nenhum usuário encontrado</p>
                    </div>
                  </template>
                </b-table>
              </div>

              <nav class="pagination-nav">
                <b-pagination
                  :value="data.page"
                  :total-rows="data.total"
                  :per-page="data.limit"
                  prev-text="Anterior"
                  next-text="Próximo"
                  align="center"
                  @input="handlePageChange"
                ></b-pagination>
              </nav>
            </b-card-body>
          </b-card>
        </transition>
      </b-col>
    </b-row>

    <!-- Sidebar para criar/editar usuário -->
    <transition name="sidebar-slide">
      <div
        v-if="showSidebar"
        class="user-sidebar-overlay"
        @click="closeSidebar"
      >
        <div class="user-sidebar" @click.stop>
          <div class="sidebar-header">
            <h5>
              {{ sidebarUserId === "novo" ? "Novo Usuário" : "Editar Usuário" }}
            </h5>
            <b-button variant="link" class="close-btn" @click="closeSidebar">
              <i class="fas fa-times"></i>
            </b-button>
          </div>
          <div class="sidebar-content">
            <UserView
              v-if="showSidebar"
              :id="sidebarUserId"
              @saved="onUserSaved"
              @cancelled="closeSidebar"
            />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import { getUsers } from "../../api/user";
import { mapGetters } from "vuex";
import UserView from "./UserView.vue";

export default {
  name: "UsersList",
  components: {
    UserView,
  },
  data() {
    return {
      showSidebar: false,
      sidebarUserId: null,
      sortBy: "createdAt",
      sortDesc: true,
      headers: [
        { key: "name", label: "Nome", sortable: true },
        { key: "company", label: "Empresa", sortable: true },
        { key: "roleName", label: "Papel", sortable: true },
        { key: "position", label: "Cargo", sortable: true },
        { key: "lastLogin", label: "Último Acesso", sortable: true },
        { key: "status", label: "Status", sortable: true },
      ],
      data: {
        docs: [],
        total: 0,
        page: 1,
        pages: 0,
        limit: 10,
      },
      filter: "",
      loading: false,
      filterTimeout: null,
    };
  },
  computed: {
    ...mapGetters(["profile", "permissions"]),
    canCreate() {
      const role = this.profile.role;
      if (parseInt(role) === role) {
        const permissions = this.permissions;
        return permissions.user && permissions.user.create;
      }
      return true;
    },
    canEdit() {
      const role = this.profile.role;
      if (parseInt(role) === role) {
        const permissions = this.permissions;
        return permissions.user && permissions.user.edit;
      }
      return true;
    },
  },
  watch: {},
  mounted() {
    this.fetchUsers();
  },
  beforeDestroy() {
    if (this.filterTimeout) {
      clearTimeout(this.filterTimeout);
    }
  },
  methods: {
    getBadge(isActive) {
      return isActive ? "success" : "secondary";
    },
    userLink(id) {
      return `usuarios/${id.toString()}`;
    },
    rowClass(item) {
      if (!item) return "";
      const baseClass = "user-row";
      return item.active ? baseClass : `${baseClass} user-inactive`;
    },
    onSortChanged(sortOptions) {
      if (sortOptions.sortBy) {
        this.sortBy = sortOptions.sortBy;
        this.sortDesc = sortOptions.sortDesc || false;
      } else {
        this.sortBy = null;
        this.sortDesc = false;
      }
      this.fetchUsers();
    },
    applyFilter() {
      this.data.page = 1;
      this.fetchUsers();
    },
    handlePageChange(page) {
      this.data.page = page;
      this.fetchUsers();
    },
    debounceFilter() {
      clearTimeout(this.filterTimeout);
      this.filterTimeout = setTimeout(() => {
        this.applyFilter();
      }, 300);
    },
    clearFilter() {
      this.filter = "";
      this.applyFilter();
    },

    edit(item) {
      this.sidebarUserId = item._id;
      this.showSidebar = true;
    },

    onRowClicked(item) {
      this.edit(item);
    },

    onNewUserClicked() {
      this.sidebarUserId = "novo";
      this.showSidebar = true;
    },

    closeSidebar() {
      this.showSidebar = false;
      this.sidebarUserId = null;
    },

    onUserSaved() {
      this.closeSidebar();
      this.fetchUsers();
    },

    async fetchUsers() {
      this.loading = true;
      try {
        const query = {
          page: this.data.page,
          size: this.data.limit,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
          filter: this.filter || undefined,
        };

        const response = await getUsers(query);
        if (response && response.data) {
          const currentPage = Number(this.data.page) || 1;
          const currentLimit = Number(this.data.limit) || 10;

          if (Array.isArray(response.data)) {
            const total = response.total || response.data.length;
            const pages = Math.max(1, Math.ceil(total / currentLimit));
            const safePage = Math.min(Math.max(currentPage, 1), pages);
            const start = (safePage - 1) * currentLimit;

            this.data.docs = response.data.slice(start, start + currentLimit);
            this.data.total = total;
            this.data.pages = pages;
            this.data.page = safePage;
            this.data.limit = currentLimit;
          } else if (response.data.docs) {
            this.data.docs = response.data.docs;
            this.data.total = response.data.total || 0;
            this.data.pages = response.data.pages || 1;
            this.data.limit =
              (Number(response.data.limit) > 0 &&
                Number(response.data.limit)) ||
              currentLimit;
            this.data.page =
              (Number(response.data.page) > 0 && Number(response.data.page)) ||
              currentPage;
          } else {
            this.data.docs = [];
            this.data.total = 0;
          }
        } else {
          this.data.docs = [];
          this.data.total = 0;
        }
      } catch (error) {
        console.error("Erro ao carregar usuários:", error);
        let errorMessage = "Erro ao carregar usuários";
        if (
          error.response &&
          error.response.data &&
          error.response.data.message
        ) {
          errorMessage = error.response.data.message;
        } else if (error.message) {
          errorMessage = error.message;
        }
        this.$bvToast.toast(errorMessage, {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
        this.data.docs = [];
        this.data.total = 0;
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.users-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.filters-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: flex-end;
}

.search-filter {
  flex: 1;
}

.filters-section .form-group {
  margin-bottom: 0;
}

.filters-section .form-control,
.filters-section .custom-select {
  border-radius: 6px;
  border: 1px solid #e9ecef;
  background-color: #fff;
  color: #495057;
  height: calc(1.5em + 1rem + 2px);
  padding: 0.5rem 1rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.filters-section .form-control:focus,
.filters-section .custom-select:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
  outline: 0;
}

.filters-section .input-group-text {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-right: 0;
  border-radius: 6px 0 0 6px;
}

.filters-section .form-group label {
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #495057;
  margin-bottom: 0.5rem;
}

.pagination-nav {
  margin-top: 1.5rem;
}

::v-deep .users-header th {
  background-color: #f8f9fa;
  font-weight: 600;
  color: #495057;
  font-size: 0.8rem;
  text-transform: uppercase;
  border: 0;
  white-space: nowrap;
}

::v-deep .users-header th.sortable:hover {
  color: #0056b3;
}

::v-deep .user-row {
  cursor: pointer;
  border-bottom: 1px solid #f1f3f4;
  transition: background-color 0.2s ease-in-out;
}

::v-deep .user-row:hover {
  background-color: #e9ecef !important;
}

::v-deep .user-row.user-inactive {
  background-color: rgba(108, 117, 125, 0.05);
}

::v-deep tbody tr:last-of-type {
  border-bottom: none;
}

::v-deep .user-row td {
  border-top: none;
  vertical-align: middle;
  padding: 0.85rem 0.75rem;
}

::v-deep .user-name {
  color: #007bff;
  font-weight: 500;
}

::v-deep .company-name,
::v-deep .creator-text,
::v-deep .date-text,
::v-deep .role-text,
::v-deep .position-text {
  color: #6c757d;
}

::v-deep .action-icon {
  color: #6c757d;
  transition: color 0.2s;
}

::v-deep .user-row:hover .action-icon {
  color: #007bff;
}

::v-deep .page-item .page-link {
  border-radius: 6px;
  margin: 0 3px;
  border: 1px solid #e9ecef;
  color: #495057;
  transition: background-color 0.2s, color 0.2s;
}

::v-deep .page-item .page-link:hover {
  background-color: #f0f2f5;
  color: #0056b3;
}

::v-deep .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
}

::v-deep .page-item.disabled .page-link {
  background-color: transparent;
  color: #adb5bd;
}

/* Sidebar styles */
.users-list-container {
  position: relative;
}

.user-sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1050;
  display: flex;
  justify-content: flex-end;
}

.user-sidebar {
  width: 50%;
  height: 100%;
  background: #f8f9fa;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  background: white;
  border-bottom: 1px solid #e9ecef;
}

.sidebar-header h5 {
  margin: 0;
  font-weight: 600;
  color: #495057;
}

.close-btn {
  padding: 0;
  color: #6c757d;
  font-size: 1.5rem;
  line-height: 1;
  transition: color 0.2s;
}

.close-btn:hover {
  color: #495057;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

/* Sidebar animations */
.sidebar-slide-enter-active,
.sidebar-slide-leave-active {
  transition: opacity 0.3s;
}

.sidebar-slide-enter-active .user-sidebar,
.sidebar-slide-leave-active .user-sidebar {
  transition: transform 0.3s ease-out;
}

.sidebar-slide-enter,
.sidebar-slide-leave-to {
  opacity: 0;
}

.sidebar-slide-enter .user-sidebar,
.sidebar-slide-leave-to .user-sidebar {
  transform: translateX(100%);
}

/* Responsive */
@media (max-width: 1200px) {
  .user-sidebar {
    width: 60%;
  }
}

@media (max-width: 768px) {
  .user-sidebar {
    width: 85%;
  }
}

@media (max-width: 576px) {
  .user-sidebar {
    width: 100%;
  }
}
</style>
